/**
 * SOLID Review
 * S — Strong. One concern: ask the ad network to fill a slot — once per mount, or at every refresh the view hands it.
 * O — Good. Another trigger is the view's input.
 * L — Strong. A view model (`IServiceViewModel`) with no state a view renders.
 * I — Strong. The view's inputs.
 * D — Strong. The platform's ad queue.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { Reactions } from './Reactions';
import type { IServiceViewAds } from './platform/IConfigDepViewPlatform';

const NO_STATE: Readonly<Record<string, never>> = Object.freeze({});

/**
 * AN AD SLOT'S FILL (frontend-framework-oo P5, 2026-10-30, §28.297) — the effects of `GoogleAdUnit.tsx` (once per mount — `once`, a
 * guard that holds across StrictMode's simulated remount, as the component's ref did) and of `AdContainer.tsx`'s AdSense slot (again at
 * every refresh: its view hands it the refresh's time). An SDK error is a safe no-op.
 */
export class AdPushModel implements IServiceViewModel<typeof NO_STATE> {
  private pushed = false;
  private readonly reactions = new Reactions<readonly unknown[]>([{
    deps: (inputs) => inputs,
    run: () => {
      if (this.once && this.pushed) return;
      try {
        this.ads.push();
        this.pushed = true;
      } catch {
        // AdSense SDK error — safe no-op.
      }
    },
  }]);

  constructor(
    private readonly ads: IServiceViewAds,
    private readonly once: boolean,
  ) {}

  getSnapshot(): typeof NO_STATE {
    return NO_STATE;
  }

  subscribe(): () => void {
    return () => undefined;
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs);
  }
}
